import { supabase } from './supabase';
import { listEventTemplatesCore, upsertEventTemplateCore, upsertTemplateFieldsCore } from '@gv/core';

export type EventTemplate = {
    id?: string;
    user_id: string;
    name: string;
    notes?: string | null;
    use_count?: number;
    last_used_at?: string;
    custom_fields?: string[]; // labels of dynamic fields
    last_values?: Record<string, string>; // last entered values for quick prefills
};

function tableMissing(err: any) {
    const msg = String(err?.message || err?.details || err);
    return /relation .*event_templates.* does not exist/i.test(msg) || /table .*event_templates.* does not exist/i.test(msg);
}

// Local cache key
const LS_KEY = 'gv_event_templates_cache_v1';

export async function listEventTemplates(userId: string): Promise<EventTemplate[]> {
    try {
        const rows = await listEventTemplatesCore(supabase as any, userId) as any as EventTemplate[];
        try { localStorage.setItem(LS_KEY, JSON.stringify(rows)); } catch { }
        return rows;
    } catch (e) {
        if (tableMissing(e)) {
            // Fall back to localStorage
            try {
                const raw = localStorage.getItem(LS_KEY);
                if (raw) return JSON.parse(raw);
            } catch { }
            return [];
        }
        console.error('listEventTemplates failed', e);
        return [];
    }
}

export async function upsertEventTemplate(userId: string, name: string, notes?: string | null): Promise<void> {
    const trimmed = (name || '').trim();
    if (!userId || !trimmed) return;
    try {
        await upsertEventTemplateCore(supabase as any, userId, trimmed, notes ?? null);
    } catch (e) {
        if (tableMissing(e)) {
            // Update local cache: merge/increment
            try {
                const raw = localStorage.getItem(LS_KEY);
                const arr: EventTemplate[] = raw ? JSON.parse(raw) : [];
                const idx = arr.findIndex(t => t.user_id === userId && t.name.toLowerCase() === trimmed.toLowerCase());
                if (idx >= 0) {
                    arr[idx].use_count = (arr[idx].use_count || 0) + 1;
                    arr[idx].notes = notes ?? arr[idx].notes ?? null;
                    arr[idx].last_used_at = new Date().toISOString();
                } else {
                    arr.push({ user_id: userId, name: trimmed, notes: notes ?? null, use_count: 1, last_used_at: new Date().toISOString() });
                }
                localStorage.setItem(LS_KEY, JSON.stringify(arr));
            } catch { }
            return;
        }
        console.error('upsertEventTemplate failed', e);
    }
}

export async function upsertTemplateFields(userId: string, name: string, fieldLabels: string[], lastValues?: Record<string, string>) {
    const trimmed = (name || '').trim();
    if (!userId || !trimmed) return;
    try {
        await upsertTemplateFieldsCore(supabase as any, userId, trimmed, fieldLabels, lastValues);
    } catch (e) {
        if (tableMissing(e)) {
            // Attempt local cache merge for custom fields
            try {
                const raw = localStorage.getItem(LS_KEY);
                const arr: EventTemplate[] = raw ? JSON.parse(raw) : [];
                const idx = arr.findIndex(t => t.user_id === userId && t.name.toLowerCase() === trimmed.toLowerCase());
                if (idx >= 0) {
                    const set = new Set<string>(Array.isArray(arr[idx].custom_fields) ? arr[idx].custom_fields : []);
                    for (const f of fieldLabels) set.add(f);
                    arr[idx].custom_fields = Array.from(set);
                    arr[idx].last_values = lastValues ?? arr[idx].last_values ?? {};
                } else {
                    arr.push({ user_id: userId, name: trimmed, custom_fields: fieldLabels, last_values: lastValues ?? {} });
                }
                localStorage.setItem(LS_KEY, JSON.stringify(arr));
            } catch { }
            return;
        }
        console.error('upsertTemplateFields failed', e);
    }
}
